import dayjs from "dayjs";
import "dayjs/locale/es";
dayjs.locale("es");

/**
 * Pdf/creditosMovimientosPdf.js
 *
 * Listado de cualquiera de las seis pestañas de "Creditos y Movimientos" (créditos INFONAVIT y
 * FONACOT, movimientos e incidencias permanentes, incapacidades y vacaciones).
 *
 * Es genérico: recibe ya aplanadas las columnas y las filas que arma `construirTabla()` en
 * CreditosMovimientos/exportarCreditosMovimientos.js — el mismo constructor que alimenta el Excel,
 * para que los dos documentos digan exactamente lo mismo. Aquí sólo se maqueta.
 *
 * Va en horizontal a propósito: los listados traen de 10 a 14 columnas y en vertical no caben.
 *
 * Paleta de marca BIWO, igual que la Lista de Raya y el resto de los reportes "modernos".
 */

const C_TURQUESA = "#00BFD6";
const C_NARANJA  = "#FF6A1A";
const C_TEAL_PRO = "#005F6B";
const C_BANDA_GR = "#BFEDF5";
const C_ZEBRA    = "#F5FBFC";
const C_TINTA_SU = "#8C8C8C";
const C_BORDE    = "#D9D9D9";

// LETTER horizontal (792 pt) menos los márgenes laterales de 22.
const ANCHO_UTIL = 748;

const fmtDinero = (n) =>
  (Number(n) || 0).toLocaleString("es-MX", {
    style: "currency", currency: "MXN", minimumFractionDigits: 2,
  });

const ALINEACION = { numero: "right", moneda: "right", centro: "center", fecha: "center" };

/** Texto de una celda ya aplanada. Las de dinero y las numéricas se formatean aquí. */
const textoCelda = ({ valor, tipo }, textoFecha) => {
  if (tipo === "moneda") {
    const n = Number(valor);
    return Number.isFinite(n) ? fmtDinero(n) : "";
  }
  if (tipo === "numero") {
    const n = Number(valor);
    return Number.isFinite(n) ? String(n) : "";
  }
  if (tipo === "fecha") return textoFecha(valor);
  return valor === null || valor === undefined ? "" : String(valor);
};

/**
 * @param {object}   p
 * @param {string}   p.titulo              Nombre del módulo ("Créditos INFONAVIT", ...).
 * @param {string}   p.empresaNombre
 * @param {string}   p.descripcionFiltros  Los filtros activos, en texto.
 * @param {object[]} p.columnas            De `definicion.columnasExport(ctx)`.
 * @param {object[][]} p.filas             Celdas `{ valor, tipo }`, paralelas a `columnas`.
 * @param {(object|null)[]} p.totales      Paralelo a `columnas`; `null` donde no se suma.
 * @param {function} p.textoFecha          Formateador de fecha compartido con el Excel.
 */
const creditosMovimientosPdf = ({
  titulo, empresaNombre, descripcionFiltros, columnas, filas, totales, textoFecha,
}) => {

  // ── Encabezado ──────────────────────────────────────────────────────────
  const encabezado = {
    columns: [
      {
        width: "*",
        stack: [
          { text: "BIWO", style: "biwo" },
          { text: "NÓMINAS", style: "nominas" },
          { text: empresaNombre || "", style: "empresa", margin: [0, 6, 0, 0] },
          { text: titulo, style: "titulo" },
          ...(descripcionFiltros ? [{ text: descripcionFiltros, style: "meta" }] : []),
        ],
      },
      {
        width: "auto",
        stack: [
          { text: `${filas.length}`, style: "contadorNum", alignment: "right" },
          { text: "registro(s)", style: "contadorTxt", alignment: "right" },
        ],
      },
    ],
  };

  const regla = {
    canvas: [{ type: "rect", x: 0, y: 0, w: ANCHO_UTIL, h: 3, color: C_TURQUESA }],
    margin: [0, 8, 0, 10],
  };

  // ── Tabla ───────────────────────────────────────────────────────────────
  // En una columna de tipo mixto (el Importe de un movimiento) el tipo declarado es una función:
  // el encabezado se alinea como su primera celda, que es la que manda visualmente.
  const alineacionCol = columnas.map((c, i) => {
    const tipo = typeof c.tipo === "function"
      ? (filas.length ? filas[0][i].tipo : "texto")
      : c.tipo;
    return ALINEACION[tipo] || "left";
  });

  const cuerpo = [
    columnas.map((c, i) => ({ text: c.titulo, style: "th", alignment: alineacionCol[i] })),
    ...filas.map((fila, i) => {
      const fill = i % 2 === 1 ? C_ZEBRA : null;
      return fila.map((cel) => ({
        text: textoCelda(cel, textoFecha),
        style: "celda",
        alignment: ALINEACION[cel.tipo] || "left",
        fillColor: fill,
      }));
    }),
  ];

  const hayTotales = totales.some(Boolean);
  if (hayTotales) {
    cuerpo.push(columnas.map((columna, i) => {
      const total = totales[i];
      const base = { fillColor: C_BANDA_GR, style: "total" };
      if (i === 0) return { ...base, text: "TOTAL" };
      if (!total) return { ...base, text: "" };
      return {
        ...base,
        text: total.tipo === "moneda" ? fmtDinero(total.valor) : String(total.valor),
        alignment: "right",
      };
    }));
  }

  const iSobreTotal = hayTotales ? cuerpo.length - 1 : -1;

  const tabla = {
    table: {
      headerRows: 1,
      widths: columnas.map((c) => c.anchoPdf ?? "auto"),
      body: cuerpo,
    },
    layout: {
      // Sólo líneas horizontales, y ninguna bajo el encabezado: el relleno turquesa ya lo separa.
      // `iSobreTotal` es la raya gruesa que va justo encima de la fila de TOTAL.
      hLineWidth: (i, node) => {
        if (i === 0 || i === 1) return 0;
        return i === iSobreTotal || i === node.table.body.length ? 0.8 : 0.4;
      },
      vLineWidth: () => 0,
      hLineColor: (i, node) =>
        (i === iSobreTotal || i === node.table.body.length ? C_TEAL_PRO : C_BORDE),
      paddingTop: () => 2.5,
      paddingBottom: () => 2.5,
      paddingLeft: () => 3,
      paddingRight: () => 3,
    },
  };

  return {
    pageSize: "LETTER",
    pageOrientation: "landscape",
    pageMargins: [22, 22, 22, 30],
    footer: (pagina, total) => ({
      columns: [
        { text: `Generado ${dayjs().format("DD/MM/YYYY HH:mm")}`, style: "pie" },
        { text: `${pagina} / ${total}`, style: "pie", alignment: "right" },
      ],
      margin: [22, 8, 22, 0],
    }),
    content: [encabezado, regla, tabla],
    styles: {
      biwo:        { fontSize: 20, bold: true, color: C_TURQUESA },
      nominas:     { fontSize: 8, bold: true, color: C_NARANJA },
      empresa:     { fontSize: 13, bold: true },
      titulo:      { fontSize: 10, bold: true, color: C_TEAL_PRO, margin: [0, 2, 0, 0] },
      meta:        { fontSize: 7, color: C_TINTA_SU, margin: [0, 2, 0, 0] },
      contadorNum: { fontSize: 22, bold: true, color: C_TURQUESA },
      contadorTxt: { fontSize: 7, color: C_TINTA_SU },
      th:          { fontSize: 7.5, bold: true, color: "#FFFFFF", fillColor: C_TURQUESA, margin: [0, 2, 0, 2] },
      celda:       { fontSize: 7 },
      total:       { fontSize: 7.5, bold: true, color: C_TEAL_PRO },
      pie:         { fontSize: 6.5, color: C_TINTA_SU },
    },
  };
};

export default creditosMovimientosPdf;
